// src/pages/NewNotificationLink.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createNotificationLink,
  getNotificationLinks,
  updateNotificationLink,
} from "../services/notificationLinks";
import { toast } from "react-toastify";
import { Copy, Link2, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { getAuthHeaders } from "../services/authHeaders";
import { PLATFORM_NOTIFICATION_PARAMS } from "../utils/platformNotificationTemplates";

const PLATFORMS_ENDPOINT = "https://api.sobevenda.com.br/api-proxy/affiliate/platforms";
const PLATFORMS_REQUIRE_SECRET = new Set(["JVZoo", "ClickBank"]);

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

export default function NewNotificationLink() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [platformList, setPlatformList] = useState([]);
  const [platform, setPlatform] = useState("");
  const [name, setName] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loadingPlatforms, setLoadingPlatforms] = useState(true);
  const [baseUrl, setBaseUrl] = useState("");
  const [secretKey, setSecretKey] = useState("");

  useEffect(() => {
    async function fetchPlatforms() {
      try {
        const { data } = await axios.get(PLATFORMS_ENDPOINT, { headers: getAuthHeaders() });
        setPlatformList(data.filter((p) => PLATFORM_NOTIFICATION_PARAMS[p.name]));
      } catch { toast.error("Erro ao carregar plataformas"); }
      finally { setLoadingPlatforms(false); }
    }
    fetchPlatforms();
  }, []);

  useEffect(() => {
    setGeneratedUrl(""); setBaseUrl(""); setSecretKey("");
  }, [platform]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const all = await getNotificationLinks();
        const link = all.find((l) => String(l.id) === id);
        if (!link) return navigate("/notification-links");
        setPlatform(link.platform);
        setName(link.name);
        setSecretKey(link.secret_key || "");
        const urlBase = link.url?.split("?")[0] || link.url || "";
        setBaseUrl(urlBase);
        setGeneratedUrl(link.url);
      } catch { toast.error("Não foi possível carregar"); navigate("/notification-links"); }
    })();
  }, [isEdit, id, navigate]);

  function generateNotificationUrl(base, platformName, params) {
    if (!base) return "";
    if (!params) params = (PLATFORM_NOTIFICATION_PARAMS[platformName] || []).join("&");
    if (!params) return base;
    return `${base}?${params}`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!platform) return toast.error("Selecione a plataforma");
    if (!name.trim()) return toast.error("Informe um nome para identificar a estrutura");
    const requiresSecret = PLATFORMS_REQUIRE_SECRET.has(platform);
    if (!isEdit && requiresSecret && !secretKey.trim()) return toast.error("Informe a Secret Key");

    try {
      const params = (PLATFORM_NOTIFICATION_PARAMS[platform] || []).join("&");
      const payload = { platform, name, params, ...(requiresSecret && secretKey.trim() ? { secret_key: secretKey.trim() } : {}) };
      let linkData;

      if (isEdit) {
        await updateNotificationLink(id, payload);
        const all = await getNotificationLinks();
        linkData = all.find((l) => String(l.id) === id);
        if (!linkData) throw new Error("Link não encontrado após salvar.");
        const urlBase = linkData.url?.split("?")[0] || linkData.url || "";
        setBaseUrl(urlBase);
        setGeneratedUrl(generateNotificationUrl(urlBase, platform, linkData.params));
        setSecretKey(linkData.secret_key || secretKey);
      } else {
        linkData = await createNotificationLink(payload);
        if (!linkData?.url) throw new Error("Link não encontrado após salvar.");
        const urlBase = linkData.url.split("?")[0];
        setBaseUrl(urlBase);
        setGeneratedUrl(generateNotificationUrl(urlBase, platform, params));
        setSecretKey(secretKey.trim());
      }
      toast.success(isEdit ? "Atualizado!" : "Criado!");
    } catch (err) { toast.error(err.message || "Erro ao salvar"); }
  };

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
    navigate("/notification-links");
  };

  const showSecretField = PLATFORMS_REQUIRE_SECRET.has(platform);

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-xl space-y-6">

        {/* HEADER */}
        <div>
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-4 transition">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Link2 className="w-6 h-6" />
            {isEdit ? "Editar Link de Notificação" : "Novo Link de Notificação"}
          </h1>
        </div>

        {/* CARD */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
          {!generatedUrl ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Campos-isca invisíveis para autofill */}
              <input type="text"     name="username" tabIndex={-1} aria-hidden="true" style={{ position:"absolute", left:"-9999px", width:1, height:1, opacity:0 }} />
              <input type="password" name="password" tabIndex={-1} aria-hidden="true" style={{ position:"absolute", left:"-9999px", width:1, height:1, opacity:0 }} />

              {/* Plataforma */}
              <div>
                <label className={labelCls} htmlFor="platform">Plataforma <span className="text-red-500">*</span></label>
                <select id="platform" value={platform} onChange={(e) => setPlatform(e.target.value)} required disabled={isEdit} className={`${inputCls} disabled:opacity-60`}>
                  <option value="">Selecione a plataforma</option>
                  {platformList.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>

              {/* Nome */}
              <div>
                <label className={labelCls} htmlFor="structure_name">Nome da estrutura / campanha <span className="text-red-500">*</span></label>
                <input id="structure_name" type="text" placeholder="ex: oferta-azul-buygoods" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
              </div>

              {/* Secret Key */}
              {showSecretField && (
                <div>
                  <label className={labelCls} htmlFor="secret_key">Secret Key <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <input id="secret_key" name="secret_key" type="text" inputMode="text" value={secretKey}
                      onChange={(e) => setSecretKey(e.target.value)} placeholder="Cole ou crie sua Secret Key" className={`${inputCls} flex-1`} />
                    <button type="button" onClick={() => { if (!secretKey) return; navigator.clipboard.writeText(secretKey); toast.success("Secret Key copiada!"); }}
                      className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 shrink-0">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Em alguns casos a plataforma fornecerá essa chave. Ou você pode criar a sua, de até 16 caracteres entre letras e números em CAIXA ALTA.
                  </p>
                </div>
              )}

              {/* Botão */}
              <button type="submit" disabled={loadingPlatforms}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                <Link2 className="w-4 h-4" />
                {isEdit ? "Salvar alterações" : "Gerar Link"}
              </button>
            </form>
          ) : (
            /* URL gerada */
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Link gerado com sucesso</p>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
                <span className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all flex-1 select-all">
                  {generatedUrl}
                </span>
                <button type="button" onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copiado!</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Use este link na plataforma de afiliados para receber notificações de vendas nesta estrutura.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}